import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@noor/config';
import * as z from 'zod';

/** A `uint64` amount: protobuf-JSON sends it as a number or a decimal string. */
const uint64Schema = z.coerce.number().nonnegative();

export const orderStatusSchema = z.enum([
  'ORDER_STATUS_UNSPECIFIED',
  'ORDER_STATUS_PENDING',
  'ORDER_STATUS_AWAITING_PAYMENT',
  'ORDER_STATUS_CONFIRMED',
  'ORDER_STATUS_PAID',
  'ORDER_STATUS_DELIVERED',
  'ORDER_STATUS_CANCELLED',
]);
export const merchantOrderStatusSchema = z.enum([
  'MERCHANT_ORDER_STATUS_UNSPECIFIED',
  'MERCHANT_ORDER_STATUS_NEW',
  'MERCHANT_ORDER_STATUS_PREPARING',
  'MERCHANT_ORDER_STATUS_READY',
  'MERCHANT_ORDER_STATUS_PICKED_UP',
  'MERCHANT_ORDER_STATUS_DELIVERED',
  'MERCHANT_ORDER_STATUS_CANCELLED',
]);
export const deliveryStageSchema = z.enum([
  'DELIVERY_STAGE_UNSPECIFIED',
  'DELIVERY_STAGE_AWAITING_MERCHANT',
  'DELIVERY_STAGE_MERCHANT_ACCEPTED',
  'DELIVERY_STAGE_COURIER_ASSIGNED',
  'DELIVERY_STAGE_PICKED_UP',
  'DELIVERY_STAGE_READY_FOR_PICKUP',
  'DELIVERY_STAGE_DELIVERED',
]);
export const stepSourceSchema = z.enum([
  'STEP_SOURCE_UNSPECIFIED',
  'STEP_SOURCE_NOOR',
  'STEP_SOURCE_IIKO',
  'STEP_SOURCE_DELEVER',
  'STEP_SOURCE_PAYMENT',
]);
export const stepStateSchema = z.enum([
  'STEP_STATE_UNSPECIFIED',
  'STEP_STATE_OK',
  'STEP_STATE_ERROR',
  'STEP_STATE_WAITING',
  'STEP_STATE_CANCELLED',
]);
export const deliveryMethodSchema = z.enum([
  'DELIVERY_METHOD_UNSPECIFIED',
  'DELIVERY_METHOD_DELIVERY',
  'DELIVERY_METHOD_PICKUP',
]);
export const adminFilterSchema = z.enum([
  'ADMIN_ORDER_FILTER_UNSPECIFIED',
  'ADMIN_ORDER_FILTER_PROBLEM',
  'ADMIN_ORDER_FILTER_CREATED',
  'ADMIN_ORDER_FILTER_AWAITING_MERCHANT',
  'ADMIN_ORDER_FILTER_PREPARING',
  'ADMIN_ORDER_FILTER_READY',
  'ADMIN_ORDER_FILTER_COURIER_ASSIGNED',
  'ADMIN_ORDER_FILTER_ON_THE_WAY',
  'ADMIN_ORDER_FILTER_COMPLETED',
  'ADMIN_ORDER_FILTER_CANCELLED',
]);
export const orderStepSchema = z.object({
  title: z.string().optional(),
  at: z.string().optional(),
  state: stepStateSchema.optional(),
  source: stepSourceSchema.optional(),
  reason: z.string().optional(),
  detail: z.string().optional(),
  code: z.string().optional(),
  request_id: z.string().optional(),
});
const geoSchema = z.object({
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});
const modifierSchema = z.object({
  group_id: z.string().optional(),
  group_name: z.string().optional(),
  options: z
    .array(
      z.object({
        option_id: z.string().optional(),
        option_name: z.string().optional(),
        amount: z.number().optional(),
      }),
    )
    .optional(),
});
export const foodOrderSchema = z.object({
  order: z
    .object({
      id: z.string().optional(),
      user_id: z.string().optional(),
      restaurant_id: z.string().optional(),
      restaurant_name: z.string().optional(),
      status: orderStatusSchema.optional(),
      delivery_stage: deliveryStageSchema.optional(),
      total_price: z.number().optional(),
      delivery_fee: z.number().optional(),
      service_fee: z.number().optional(),
      payment_type: z.enum(['PAYMENT_TYPE_UNSPECIFIED', 'PAYMENT_TYPE_CARD']).optional(),
      delivery_method: deliveryMethodSchema.optional(),
      delivery_address: z.string().optional(),
      delivery_geo: geoSchema.optional(),
      restaurant_geo: geoSchema.optional(),
      leave_at_door: z.boolean().optional(),
      courier_comment: z.string().optional(),
      restaurant_comment: z.string().optional(),
      phone_number: z.string().optional(),
      scheduled_at: z.string().optional(),
      created_at: z.string().optional(),
      updated_at: z.string().optional(),
      items: z
        .array(
          z.object({
            product_id: z.string().optional(),
            name: z.string().optional(),
            price: z.number().optional(),
            quantity: z.number().optional(),
            image: z.string().optional(),
            external_id: z.string().optional(),
            item_id: z.string().optional(),
            selected_modifiers: z.array(modifierSchema).optional(),
          }),
        )
        .optional(),
      // These fields are open strings in the API, not enums.
      dispatch_status: z.string().optional(),
      delivery_provider: z.string().optional(),
      last_payment_error: z.string().optional(),
      // Eats order numbers can be formatted strings (e.g. "02-100068") or
      // numeric IDs from older API responses. Keep the original representation.
      order_number: z.union([z.string(), z.number()]).optional(),
      merchant_accepted_at: z.string().optional(),
      ready_at: z.string().optional(),
      picked_up_by_courier_at: z.string().optional(),
      entrance: z.string().optional(),
      floor: z.string().optional(),
      apartment: z.string().optional(),
      eta_min_minutes: z.number().optional(),
      eta_max_minutes: z.number().optional(),
      payment_card_mask: z.string().optional(),
      payment_card_type: z.string().optional(),
      version: z.number().optional(),
      courier_pickup_code: z.string().optional(),
      cancel_reason: z.string().optional(),
      courier: z
        .object({
          name: z.string().optional(),
          phone: z.string().optional(),
          tracking_url: z.string().optional(),
          courier_id: z.number().optional(),
        })
        .optional(),
      review: z
        .object({
          id: z.string().optional(),
          rating: z.number().optional(),
          comment: z.string().optional(),
          created_at: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  merchant_order_status: merchantOrderStatusSchema.optional(),
  merchant_decision_deadline: z.string().optional(),
  courier_pickup_at: z.string().optional(),
  /** Full delivery cost before the brand's split; `order.delivery_fee` is the customer's part. */
  delivery_cost: uint64Schema.optional(),
  /** The merchant's part of `delivery_cost`, withheld from the brand's payout. */
  merchant_delivery_share: uint64Schema.optional(),
  cancelled_by: z.string().optional(),
  cancelled_by_id: z.string().optional(),
  noor_dispatch: z
    .object({
      noor_order_id: z.number().optional(),
      dispatch_state: z.string().optional(),
      stage: z.number().optional(),
      attempts: z.number().optional(),
      next_attempt_at: z.string().optional(),
      error: z.string().optional(),
    })
    .optional(),
  steps: z.array(orderStepSchema).optional(),
  problem: orderStepSchema.optional(),
});
export const foodOrderListSchema = z.object({
  list: z.array(foodOrderSchema).optional(),
  data: z
    .object({
      admin_counts: z
        .object({
          all: z.number().optional(),
          problem: z.number().optional(),
          in_flight: z.number().optional(),
          created: z.number().optional(),
          awaiting_merchant: z.number().optional(),
          preparing: z.number().optional(),
          ready: z.number().optional(),
          courier_assigned: z.number().optional(),
          on_the_way: z.number().optional(),
          completed: z.number().optional(),
          cancelled: z.number().optional(),
        })
        .optional(),
      counts: z
        .object({
          all: z.number().optional(),
          new: z.number().optional(),
          preparing: z.number().optional(),
          completed: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
  meta: z
    .object({
      total: z.number().optional(),
      page: z.number().optional(),
      page_count: z.number().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});
export const foodOrderDetailSchema = z.object({
  data: z.object({ order: foodOrderSchema.optional() }).optional(),
});
const dateFilter = z.iso.date().catch('').optional();
export const foodOrderSearchSchema = z.object({
  scope: z
    .enum(['by-merchant', 'by-restaurant', 'by-user'])
    .catch('by-merchant')
    .default('by-merchant'),
  entityId: z.string().optional(),
  search: z.string().optional(),
  from: dateFilter,
  to: dateFilter,
  filter: adminFilterSchema
    .catch('ADMIN_ORDER_FILTER_UNSPECIFIED')
    .default('ADMIN_ORDER_FILTER_UNSPECIFIED'),
  source: stepSourceSchema.optional().catch(undefined),
  page: z.number().int().positive().catch(1).default(1),
  limit: z.literal(PAGE_SIZE_OPTIONS).catch(DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  orderId: z.string().optional(),
});
export type FoodOrder = z.infer<typeof foodOrderSchema>;
export type OrderStep = z.infer<typeof orderStepSchema>;
export type FoodOrderSearchInput = z.input<typeof foodOrderSearchSchema>;
export type FoodOrderSearch = z.infer<typeof foodOrderSearchSchema>;
export const cancelFoodOrderSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'Укажите причину отмены')
    .refine((value) => !value.includes('\0'), 'Недопустимый символ'),
});
export const createTestFoodOrderSchema = z.object({
  restaurant_id: z.uuid('Укажите UUID ресторана'),
  product_id: z.union([z.uuid('Укажите UUID продукта'), z.literal('')]).optional(),
  quantity: z.number().int().min(1, 'Минимум 1'),
  delivery_method: z.enum(['DELIVERY_METHOD_DELIVERY', 'DELIVERY_METHOD_PICKUP']),
});
export type CancelFoodOrder = z.infer<typeof cancelFoodOrderSchema>;
export type CreateTestFoodOrder = z.infer<typeof createTestFoodOrderSchema>;
